"use client";

/**
 * Shared calculator controls, lifted out of MortgageCalculator so the
 * rent-versus-buy page uses the same shells (owner, 2026-09-21: "take
 * the logic of the mortgage form").
 *
 * Field: label over a bordered input with a trailing unit and an
 * optional slider twin. DownPaymentField: the same shell with the
 * percent / AZN toggle, its slider and the "= x" echo.
 */

import { digitsOnly, groupDigits } from "@/lib/number-input";
import { formatPrice } from "@/lib/api";
import { FIELD, SEGMENT_OFF, SEGMENT_ON, rangeFill } from "@/lib/ui-classes";

export function Field({
  label,
  value,
  onChange,
  unit,
  group = false,
  slider,
  error = false,
  hint,
  size = "md",
  className = "",
  ...input
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  unit: string;
  /** Money fields: show thousand groups ("60 000") while typing. */
  group?: boolean;
  /** Draggable twin of the field: a range input bound to the same value. */
  slider?: { min: number; max: number; step: number };
  /** Red border for a failed check. */
  error?: boolean;
  /** Quiet line under the field. */
  hint?: React.ReactNode;
  /** Extra classes on the wrapper (grid spans). */
  className?: string;
  /** "sm" packs the field for dense forms (rent versus buy); "auto" is
   *  packed while the fields pair up and full size from lg, where the
   *  input column stands alone and small text only looks cramped. The
   *  slider shows at every width: a control that exists on desktop
   *  exists on a phone (owner, 2026-09-21). */
  size?: "md" | "sm" | "auto";
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value" | "size">) {
  const lo = input.min != null ? Number(input.min) : undefined;
  const hi = input.max != null ? Number(input.max) : undefined;
  // Clamp out-of-range entries to the boundary; pass in-range values
  // through untouched so partial decimals ("0.", "0.5") still type.
  const handle = (input_raw: string) => {
    const raw = group ? digitsOnly(input_raw) : input_raw;
    if (raw === "") return onChange("");
    const n = Number(raw);
    if (Number.isNaN(n)) return;
    if (lo != null && n < lo) return onChange(String(lo));
    if (hi != null && n > hi) return onChange(String(hi));
    onChange(raw);
  };
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] font-semibold text-gray-500 mb-1">
        {label}
      </span>
      <span
        className={`flex items-center rounded-lg border ${
          error
            ? "field-surface border-rose-300 bg-white focus-within:border-rose-400 focus-within:ring-1 focus-within:ring-rose-200"
            : FIELD
        }`}
      >
        <input
          type={group ? "text" : "number"}
          inputMode={group ? "numeric" : "decimal"}
          value={group ? groupDigits(value) : value}
          onChange={(e) => handle(e.target.value)}
          className={`w-full bg-transparent font-semibold text-gray-900 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            size === "sm" ? "px-2.5 py-1.5 text-[13px]"
              : size === "auto" ? "px-2.5 py-1.5 text-[13px] lg:px-3 lg:py-2.5 lg:text-sm"
              : "px-3 py-2.5 text-sm"
          }`}
          {...input}
        />
        {unit !== "" && (
          <span className={`flex shrink-0 items-center text-xs leading-none text-gray-400 ${size === "sm" ? "px-2" : size === "auto" ? "px-2 lg:px-3" : "px-3"}`}>{unit}</span>
        )}
      </span>
      {slider && (() => {
        const v = Math.min(slider.max, Math.max(slider.min, Number(value) || slider.min));
        return (
          <input
            type="range"
            min={slider.min}
            max={slider.max}
            step={slider.step}
            value={v}
            onChange={(e) => onChange(e.target.value)}
            aria-label={label}
            className="mt-2 w-full"
            style={rangeFill(v, slider.min, slider.max)}
          />
        );
      })()}
      {hint && <span className="mt-1 block text-[11px] text-gray-400 leading-snug">{hint}</span>}
    </label>
  );
}

export type DownMode = "pct" | "abs";

/** Down payment as % of price or an absolute AZN amount, with the
 *  toggle, the slider and the "= other representation" echo. The value
 *  is the raw entry in the current mode; the parent converts on toggle. */
export function DownPaymentField({
  label,
  price,
  mode,
  value,
  onModeChange,
  onChange,
  size = "md",
  className = "",
}: {
  label: string;
  price: number;
  mode: DownMode;
  value: string;
  onModeChange: (m: DownMode) => void;
  onChange: (v: string) => void;
  /** "sm" packs the field and drops the slider (dense forms); "auto"
   *  is packed below lg and full size, with the slider, from lg up. */
  size?: "md" | "sm" | "auto";
  /** Extra classes on the wrapper (grid spans). */
  className?: string;
}) {
  const n = Number(value) || 0;
  const downAmount = mode === "pct" ? (price * n) / 100 : n;
  const downPct = mode === "abs" ? (price > 0 ? (n / price) * 100 : 0) : n;
  const toggle = (compact: boolean) => (
    <div className={`inline-flex rounded-md bg-gray-100 p-0.5 font-semibold ${compact ? "text-[10px]" : "text-[11px]"}`}>
      {(["pct", "abs"] as const).map((m) => (
        <button
          key={m}
          type="button"
          aria-pressed={mode === m}
          onClick={() => onModeChange(m)}
          className={`rounded transition-colors ${compact ? "px-1.5 py-px" : "px-2 py-0.5"} ${
            mode === m ? SEGMENT_ON : SEGMENT_OFF
          }`}
        >
          {m === "pct" ? "%" : "AZN"}
        </button>
      ))}
    </div>
  );
  return (
    <div className={className}>
      {size === "md" ? (
        <div className="flex items-center justify-between mb-1">
          <span className="text-[11px] font-semibold text-gray-500">{label}</span>
          {toggle(false)}
        </div>
      ) : (
        <span className="block text-[11px] font-semibold text-gray-500 mb-1">{label}</span>
      )}
      <div className={`flex items-center rounded-lg border ${FIELD}`}>
        <input
          type={mode === "pct" ? "number" : "text"}
          inputMode={mode === "pct" ? "decimal" : "numeric"}
          min={0}
          max={mode === "pct" ? 100 : price || undefined}
          value={mode === "pct" ? value : groupDigits(value)}
          onChange={(e) => {
            const raw = mode === "pct" ? e.target.value : digitsOnly(e.target.value);
            if (raw === "") return onChange("");
            const v = Number(raw);
            if (Number.isNaN(v)) return;
            if (v < 0) return onChange("0");
            const hi = mode === "pct" ? 100 : price;
            if (mode === "pct" || price > 0) {
              if (v > hi) return onChange(String(Math.round(hi)));
            }
            onChange(raw);
          }}
          className={`w-full min-w-0 bg-transparent font-semibold text-gray-900 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            size === "sm" ? "px-2.5 py-1.5 text-[13px]"
              : size === "auto" ? "px-2.5 py-1.5 text-[13px] lg:px-3 lg:py-2.5 lg:text-sm"
              : "px-3 py-2.5 text-sm"
          }`}
        />
        {/* The other unit reads inside the box (owner, 2026-09-21): as a
            line underneath it appeared with the first price and shifted
            every field below it. */}
        {price > 0 && (
          <span className="shrink-0 whitespace-nowrap pr-1.5 text-[11px] tabular-nums text-gray-400">
            {mode === "pct" ? formatPrice(Math.round(downAmount)) : `${Math.round(downPct)}%`}
          </span>
        )}
        {size === "md" ? (
          <span className="px-3 text-xs text-gray-400">{mode === "pct" ? "%" : "AZN"}</span>
        ) : (
          <span className="flex shrink-0 items-center pr-1.5 leading-none">{toggle(true)}</span>
        )}
      </div>
      {size !== "sm" && (() => {
        const hi = mode === "pct" ? 90 : Math.max(price, 1);
        const v = Math.min(hi, n);
        return (
          <input
            type="range"
            min={0}
            max={hi}
            step={mode === "pct" ? 1 : 1_000}
            value={v}
            onChange={(e) => onChange(e.target.value)}
            aria-label={label}
            className="mt-2 w-full"
            style={rangeFill(v, 0, hi)}
          />
        );
      })()}

    </div>
  );
}


/** A labelled slider with its value printed in the label row: for
 *  whole-number spans (years) where a text box is heavier than needed. */
export function RangeField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  unit,
  className = "",
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  unit: string;
  /** Extra classes on the wrapper (grid spans). */
  className?: string;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 flex items-baseline justify-between text-[11px] font-semibold text-gray-500">
        {label}
        <span className="tabular-nums text-gray-900">{value} {unit}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={Math.min(max, Math.max(min, value))}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
        className="mt-1.5 w-full"
        style={rangeFill(Math.min(max, Math.max(min, value)), min, max)}
      />
    </label>
  );
}
